/* eslint-disable no-restricted-syntax */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Typography, Grid2 as Grid, RadioGroup, Stack, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { debounce } from 'lodash';
import { PricingSurchargeAttributes, PricingSurchargeCalculationType, PricingSurchargeCategory } from '@idearoom/types';
import { PricingCategories, shedPricingCategories, SurchargeUpdateProperty } from '../../constants/PricingSurcharge';
import { AppState } from '../../types/AppState';
import { SurchargeSelectableChip } from './SurchargeSelectableChip';
import { isCarportView } from '../../utils/clientIdUtils';
import { I18nKeys } from '../../constants/I18nKeys';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { CommonSurchargeUpdateProperty, updateSurchargeProperty } from '../../ducks/pricingSlice';
import { usePricingRepo } from '../../hooks/usePricingRepo';
import { RadioField } from '../redux-form/RadioField';
import { LabeledCheckboxField } from '../library/LabeledCheckboxField';
import { InputField } from '../library/InputField';

export const SurchargeCalculationSection: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { clientId } = useAppSelector((state: AppState) => state.clientData);
  const isCarportViewClient = isCarportView(clientId);

  const { selectedPricingSurcharge } = usePricingRepo({
    useBranches: true,
    useSelectedPricingSurcharge: true,
  });
  const { key: pricingSelection, categories, label, isTaxable, affectsDeposit } = selectedPricingSurcharge;

  const [labelValue, setLabelValue] = useState(label);
  const [isTaxableValue, setIsTaxableValue] = useState(isTaxable);
  const [affectsDepositValue, setAffectsDepositValue] = useState(affectsDeposit);

  const handleCategoryChange = useCallback(
    (pricingCategory: PricingSurchargeCategory) => {
      dispatch(updateSurchargeProperty([{ property: SurchargeUpdateProperty.Categories, value: pricingCategory }]));
    },
    [dispatch],
  );
  const handleCalculationChange = useCallback(
    (prop: keyof PricingSurchargeAttributes, value: string | boolean) => {
      dispatch(updateSurchargeProperty([{ property: prop as CommonSurchargeUpdateProperty, value }]));
    },
    [dispatch],
  );

  const debouncedCalculationChangeHandler = useMemo(() => debounce(handleCalculationChange, 400), []);

  // Stop the invocation of the debounced function after unmounting
  useEffect(
    () => () => {
      debouncedCalculationChangeHandler.cancel();
    },
    [],
  );

  return (
    <Stack spacing="23px">
      {isCarportViewClient ? (
        <Stack spacing="7px">
          <Typography>{t(I18nKeys.PricingCalculationInclude)}</Typography>
          <Grid key="category-grid" container direction="row" alignItems="flex-start" spacing="8px">
            {PricingCategories.map((category) => (
              <Grid key={`${category.value}-grid`}>
                <SurchargeSelectableChip
                  chip={category}
                  selected={categories.includes(category.value as PricingSurchargeCategory)}
                  handleChipSelection={handleCategoryChange}
                />
              </Grid>
            ))}
          </Grid>
        </Stack>
      ) : (
        <RadioGroup
          aria-label="pricing-calculations"
          name="pricingCalculations"
          value={categories[0]}
          onChange={(_, value) => handleCategoryChange(value as PricingSurchargeCategory)}
          sx={{ my: '-4px !important', ml: '-4px !important' }}
        >
          {shedPricingCategories.map((option) => (
            <RadioField
              key={option.value}
              value={option.value}
              label={t(
                pricingSelection === PricingSurchargeCalculationType.Surcharge
                  ? option.surchargeLabel
                  : option.lineItemLabel,
              )}
              sx={{
                py: '4px !important',
              }}
            />
          ))}
        </RadioGroup>
      )}
      {pricingSelection === PricingSurchargeCalculationType.Surcharge ? (
        <Stack direction="row" spacing="22px">
          <Box sx={{ width: '354px' }}>
            <InputField
              label={t(I18nKeys.PricingCalculationLineItemDescription)}
              value={labelValue}
              onChange={(e) => {
                setLabelValue(e.target.value);
                debouncedCalculationChangeHandler(SurchargeUpdateProperty.Label, e.target.value);
              }}
            />
          </Box>
          <Stack direction="row" spacing="27px">
            <Box alignContent="center">
              <LabeledCheckboxField
                name="taxable"
                checked={!!isTaxableValue}
                onChange={(e) => {
                  setIsTaxableValue(e.currentTarget.checked);
                  debouncedCalculationChangeHandler(SurchargeUpdateProperty.IsTaxable, e.currentTarget.checked);
                }}
                label={t(I18nKeys.PricingCalculationTaxable)}
              />
            </Box>
            {isCarportViewClient && (
              <Box alignContent="center">
                <LabeledCheckboxField
                  name="affects-dealer-deposit"
                  checked={!!affectsDepositValue}
                  onChange={(e) => {
                    setAffectsDepositValue(e.currentTarget.checked);
                    debouncedCalculationChangeHandler(SurchargeUpdateProperty.AffectsDeposit, e.currentTarget.checked);
                  }}
                  label={t(I18nKeys.PricingCalculationIncludeDealerDeposit)}
                />
              </Box>
            )}
          </Stack>
        </Stack>
      ) : (
        <Typography color="textSecondary" variant="body2" mt="-3px">
          {isCarportViewClient
            ? t(I18nKeys.PricingCalculationCarportTaxation)
            : t(I18nKeys.PricingCalculationShedTaxation)}
        </Typography>
      )}
    </Stack>
  );
};
